import { useState } from 'react';
import { Divider } from '../../components/Divider';
import { InfoNote } from '../../components/InfoNote';
import { PageHeading } from '../../components/PageHeading';
import { PanelCard } from '../../components/PanelCard';
import { SkeletonCard } from '../../components/SkeletonCard';
import { TimeslotChipSelector } from '../../components/TimeslotChipSelector';
import { TimeChangeIcon } from '../../icons/TimeChangeIcon';
import { AllOptionsTimeslotPanel } from '../AllOptionsTimeslotPanel';
import { GuestDetailsPanel } from '../GuestDetailsPanel';
import { MultiOptionTimeslotPanel } from '../MultiOptionTimeslotPanel';
import { PreferencesPanel } from '../PreferencesPanel';
import { ReservationPanel } from '../ReservationPanel';
import { SingleOptionTimeslotPanel } from '../SingleOptionTimeslotPanel';
import {
  ScrollContainer,
  SkeletonContainer,
  TimeslotDivider,
} from './SpaDaysBookingContent.styles';
import type { SpaDaysBookingContentProps } from './SpaDaysBookingContent.types';

const defaultNoteIcon = <TimeChangeIcon size={18} />;

export function SpaDaysBookingContent({
  title,
  subtitle,
  isLoading = false,
  guestCount,
  minGuests,
  maxGuests,
  guestStep,
  onGuestCountChange,
  activeDate,
  selectedDate,
  calendarView,
  availability,
  onDateSelect,
  onWeekNavigate,
  onCalendarViewChange,
  calendarLabels,
  arrivalTimeSlots,
  selectedArrivalTime,
  onArrivalTimeSelect,
  arrivalTimeError,
  noteText,
  noteIcon = defaultNoteIcon,
  guests,
  onExistingGuestSelect,
  onGuestChange,
  titleOptions,
  genderOptions,
  onAllOptionsTimeslotChange,
  onSingleOptionSelect,
  onSingleOptionTimeslotChange,
  onMultiOptionToggle,
  onMultiOptionTimeslotChange,
  preferenceCategories,
  onPreferenceOptionToggle,
  onPreferenceOtherChange,
  onPreferenceFreeTextChange,
  labels,
}: SpaDaysBookingContentProps) {
  const selectedGuestIds = guests
    .map((g) => g.selectedGuestId)
    .filter((id): id is string => id !== undefined);

  const hasArrivalTimeChips = Boolean(onArrivalTimeSelect && arrivalTimeSlots?.length);
  const awaitingArrivalTimeSelection = hasArrivalTimeChips && !selectedArrivalTime;

  const [noteDismissed, setNoteDismissed] = useState(false);

  return (
    <ScrollContainer>
      <PageHeading title={title} subtitle={subtitle} centered mobileSpacing="compact" />

      <PanelCard>
        <ReservationPanel
          title={labels?.reservation?.title}
          subtitle={labels?.reservation?.subtitle}
          guestLabel={labels?.reservation?.guestLabel}
          guestCount={guestCount}
          minGuests={minGuests}
          maxGuests={maxGuests}
          guestStep={guestStep}
          onGuestCountChange={onGuestCountChange}
          activeDate={activeDate}
          selectedDate={selectedDate}
          calendarView={calendarView}
          availability={availability}
          onDateSelect={onDateSelect}
          onWeekNavigate={onWeekNavigate}
          onCalendarViewChange={onCalendarViewChange}
          calendarLabels={calendarLabels}
          counterDecreaseLabel={labels?.counter?.decrease}
          counterIncreaseLabel={labels?.counter?.increase}
        />
        {hasArrivalTimeChips && arrivalTimeSlots && onArrivalTimeSelect && (
          <>
            <TimeslotDivider />
            <TimeslotChipSelector
              timeslots={arrivalTimeSlots}
              selectedTimeslot={selectedArrivalTime}
              onTimeslotSelect={onArrivalTimeSelect}
              labels={labels?.timeslotChips}
              hasError={!!arrivalTimeError}
              errorText={arrivalTimeError}
            />
            {noteText && !noteDismissed && (
              <InfoNote
                text={noteText}
                icon={noteIcon}
                onDismiss={() => setNoteDismissed(true)}
                dismissLabel={labels?.noteDismissLabel}
              />
            )}
          </>
        )}
      </PanelCard>

      {!awaitingArrivalTimeSelection && (
        <>
          {isLoading ? (
            Array.from({ length: guestCount }, (_, i) => (
              <SkeletonContainer key={i}>
                <SkeletonCard variant="simple" />
                <SkeletonCard variant="simple" />
              </SkeletonContainer>
            ))
          ) : (
            <>
              {guests.map((guest, guestIndex) => {
                const disabledGuestIds = selectedGuestIds.filter(
                  (id) => id !== guest.selectedGuestId
                );

                return (
                  <PanelCard key={guest.guestNumber}>
                    <GuestDetailsPanel
                      guestNumber={guest.guestNumber}
                      existingGuests={guest.existingGuests}
                      selectedGuestId={guest.selectedGuestId}
                      disabledGuestIds={disabledGuestIds}
                      onExistingGuestSelect={(guestId) =>
                        onExistingGuestSelect(guestIndex, guestId)
                      }
                      values={guest.values}
                      onChange={(field, value) => onGuestChange(guestIndex, field, value)}
                      titleOptions={titleOptions}
                      genderOptions={genderOptions}
                      errors={guest.errors}
                      labels={labels?.guestDetails}
                    />

                    {guest.optionsPanels.map((panel, panelIndex) => (
                      <div key={panelIndex}>
                        <Divider spacing="large" />
                        {panel.type === 'all' && (
                          <AllOptionsTimeslotPanel
                            title={panel.title}
                            subtitle={panel.subtitle}
                            options={panel.options}
                            onTimeslotChange={(optionId, value) =>
                              onAllOptionsTimeslotChange(guestIndex, panelIndex, optionId, value)
                            }
                            noteText={panel.noteText}
                            noteIcon={panel.noteIcon}
                            timeslotPlaceholder={panel.timeslotPlaceholder}
                            timeslotAriaLabelFormatter={labels?.timeslotAriaLabelFormatter}
                            timeslotErrors={panel.timeslotErrors}
                            timeslotNotices={panel.timeslotNotices}
                          />
                        )}
                        {panel.type === 'single' && (
                          <SingleOptionTimeslotPanel
                            title={panel.title}
                            subtitle={panel.subtitle}
                            options={panel.options}
                            selectedOptionId={panel.selectedOptionId}
                            selectedTimeslot={panel.selectedTimeslot}
                            selectionCountText={panel.selectionCountText}
                            onOptionSelect={(optionId) =>
                              onSingleOptionSelect(guestIndex, panelIndex, optionId)
                            }
                            onTimeslotChange={(value) =>
                              onSingleOptionTimeslotChange(guestIndex, panelIndex, value)
                            }
                            noteText={panel.noteText}
                            noteIcon={panel.noteIcon}
                            timeslotPlaceholder={panel.timeslotPlaceholder}
                            timeslotHeading={panel.timeslotHeading}
                            timeslotAriaLabelFormatter={labels?.timeslotAriaLabelFormatter}
                            optionError={panel.optionError}
                            timeslotError={panel.timeslotError}
                            timeslotNotice={panel.timeslotNotice}
                          />
                        )}
                        {panel.type === 'multi' && (
                          <MultiOptionTimeslotPanel
                            title={panel.title}
                            subtitle={panel.subtitle}
                            options={panel.options}
                            maximumSelections={panel.maximumSelections}
                            selectionCountText={panel.selectionCountText}
                            onOptionToggle={(optionId) =>
                              onMultiOptionToggle(guestIndex, panelIndex, optionId)
                            }
                            onTimeslotChange={(optionId, value) =>
                              onMultiOptionTimeslotChange(guestIndex, panelIndex, optionId, value)
                            }
                            noteText={panel.noteText}
                            noteIcon={panel.noteIcon}
                            timeslotPlaceholder={panel.timeslotPlaceholder}
                            timeslotHeading={panel.timeslotHeading}
                            timeslotAriaLabelFormatter={labels?.timeslotAriaLabelFormatter}
                            optionError={panel.optionError}
                            timeslotErrors={panel.timeslotErrors}
                            timeslotNotices={panel.timeslotNotices}
                          />
                        )}
                      </div>
                    ))}
                  </PanelCard>
                );
              })}

              <PanelCard>
                <PreferencesPanel
                  categories={preferenceCategories}
                  onOptionToggle={onPreferenceOptionToggle}
                  onOtherChange={onPreferenceOtherChange}
                  onFreeTextChange={onPreferenceFreeTextChange}
                  otherButtonLabel={labels?.preferences?.otherButtonLabel}
                />
              </PanelCard>
            </>
          )}
        </>
      )}
    </ScrollContainer>
  );
}
